import { useState } from 'react';
import { getSupabaseClient } from '../lib/supabase';
import { Package } from '../components/packages/PackageList';
import { PackageFormData } from '../components/packages/PackageForm';

export const usePackages = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [guideId, setGuideId] = useState<string | null>(null);

  const loadPackages = async () => {
    try {
      const supabase = getSupabaseClient();

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) return;
      setGuideId(user.id);

      // Get packages
      const { data: packagesData, error: packagesError } = await supabase
        .from('guide_packages')
        .select('*')
        .eq('guide_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (packagesError) throw packagesError;

      // Get components for each package
      const packagesWithComponents = await Promise.all(
        (packagesData || []).map(async (pkg) => {
          const { data: components, error: componentsError } = await supabase
            .from('guide_package_components')
            .select('*')
            .eq('package_id', pkg.id)
            .order('sort_order', { ascending: true });

          if (componentsError) throw componentsError;

          const totalCost = (components || []).reduce((sum, comp) => 
            sum + (comp.quantity * comp.unit_price_dollars), 0);

          return {
            ...pkg,
            components: components || [],
            totalCost
          };
        })
      );

      setPackages(packagesWithComponents);
    } catch (error) {
      console.error('Error loading packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const createPackage = async (formData: PackageFormData) => {
    try {
      const supabase = getSupabaseClient();

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not logged in');

      // Create new package
      const { data: newPackage, error: packageError } = await supabase
        .from('guide_packages')
        .insert({
          guide_id: user.id,
          name: formData.name,
          description: formData.description,
          duration_months: formData.duration_months,
          is_active: true
        })
        .select('id')
        .single();

      if (packageError) throw packageError;

      // Insert components
      const componentsToInsert = formData.components.map(comp => ({
        package_id: newPackage.id,
        component_type: comp.component_type,
        name: comp.name,
        quantity: comp.quantity,
        unit_price_dollars: comp.unit_price_dollars,
        sort_order: comp.sort_order,
        session_length_minutes: comp.session_length_minutes ?? 60,
        session_link: comp.session_link || null
      }));

      await supabase
        .from('guide_package_components')
        .insert(componentsToInsert);

      await loadPackages();
    } catch (error) {
      console.error('Error creating package:', error);
      throw error;
    }
  };

  const updatePackage = async (packageId: string, formData: PackageFormData) => {
    try {
      const supabase = getSupabaseClient();

      // Update package
      const { error: packageError } = await supabase
        .from('guide_packages')
        .update({
          name: formData.name,
          description: formData.description,
          duration_months: formData.duration_months
        })
        .eq('id', packageId);

      if (packageError) {
        console.error('Error updating package:', packageError);
        throw packageError;
      }

      // Delete existing components
      const { error: deleteError } = await supabase
        .from('guide_package_components')
        .delete()
        .eq('package_id', packageId);

      if (deleteError) {
        console.error('Error deleting components:', deleteError);
        throw deleteError;
      }

      // Insert updated components (only if there are any)
      if (formData.components.length > 0) {
        const componentsToInsert = formData.components.map(comp => ({
          package_id: packageId,
          component_type: comp.component_type,
          name: comp.name,
          quantity: comp.quantity,
          unit_price_dollars: comp.unit_price_dollars,
          sort_order: comp.sort_order,
          session_length_minutes: comp.session_length_minutes ?? 60,
          session_link: comp.session_link || null
        }));

        const { error: insertError } = await supabase
          .from('guide_package_components')
          .insert(componentsToInsert);

        if (insertError) {
          console.error('Error inserting components:', insertError);
          throw insertError;
        }
      }

      await loadPackages();
    } catch (error) {
      console.error('Error updating package:', error);
      throw error;
    }
  };

  const deletePackage = async (packageId: string) => {
    try {
      const supabase = getSupabaseClient();

      // Delete components first
      await supabase
        .from('guide_package_components')
        .delete()
        .eq('package_id', packageId);

      // Delete package
      await supabase
        .from('guide_packages')
        .delete()
        .eq('id', packageId);

      await loadPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      throw error;
    }
  };

  return {
    packages,
    loading,
    guideId,
    loadPackages,
    createPackage,
    updatePackage,
    deletePackage
  };
};